import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2, Save, Pencil, ExternalLink, Store, ChevronDown, ChevronUp, Link2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCK_TYPES = [
  { value: 'hero', label: 'Hero Section' },
  { value: 'text', label: 'Text Block' },
  { value: 'image', label: 'Image' },
  { value: 'image_text', label: 'Image + Text' },
  { value: 'category_grid', label: 'Category Grid' },
  { value: 'featured_products', label: 'Featured Products' },
  { value: 'product_carousel', label: 'Product Carousel' },
  { value: 'trade_cta', label: 'Trade CTA' },
  { value: 'banner', label: 'Banner' },
  { value: 'stats', label: 'Stats/Numbers' },
  { value: 'features', label: 'Features Grid' },
  { value: 'testimonials', label: 'Testimonials' },
  { value: 'faq', label: 'FAQ Section' },
  { value: 'contact_form', label: 'Contact Form' },
  { value: 'map', label: 'Google Map' },
  { value: 'video', label: 'Video Embed' },
  { value: 'spacer', label: 'Spacer' },
  { value: 'divider', label: 'Divider' },
  { value: 'vendor_showcase', label: 'Vendor Showcase' },
];

function SortableBlock({ block, index, updateBlock, removeBlock }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const renderBlockFields = () => {
    switch (block.type) {
      case 'hero':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={block.content?.title || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Subtitle</Label>
              <Input
                value={block.content?.subtitle || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, subtitle: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">CTA Text</Label>
                <Input
                  value={block.content?.cta_text || ''}
                  onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, cta_text: e.target.value } })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-xs">CTA Link</Label>
                <Input
                  value={block.content?.cta_link || ''}
                  onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, cta_link: e.target.value } })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Background Image URL</Label>
              <Input
                value={block.content?.background_image || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, background_image: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        );
      case 'text':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title (optional)</Label>
              <Input
                value={block.content?.title || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Content</Label>
              <Textarea
                value={block.content?.text || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, text: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                rows={4}
              />
            </div>
          </div>
        );
      case 'image':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Image URL</Label>
              <Input
                value={block.content?.src || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, src: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Alt Text</Label>
              <Input
                value={block.content?.alt || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, alt: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        );
      case 'category_grid':
      case 'featured_products':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Section Title</Label>
              <Input
                value={block.content?.title || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Items to Show</Label>
              <Input
                type="number"
                value={block.content?.count || block.content?.show_count || 6}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, count: parseInt(e.target.value), show_count: parseInt(e.target.value) } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        );
      case 'trade_cta':
      case 'banner':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={block.content?.title || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea
                value={block.content?.description || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, description: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">CTA Text</Label>
                <Input
                  value={block.content?.cta_text || ''}
                  onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, cta_text: e.target.value } })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-xs">CTA Link</Label>
                <Input
                  value={block.content?.cta_link || ''}
                  onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, cta_link: e.target.value } })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
          </div>
        );
      case 'image_text':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={block.content?.title || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Content</Label>
              <Textarea
                value={block.content?.text || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, text: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                rows={3}
              />
            </div>
            <div>
              <Label className="text-xs">Image URL</Label>
              <Input
                value={block.content?.image || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, image: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Image Position</Label>
              <Select 
                value={block.content?.image_position || 'left'} 
                onValueChange={(v) => updateBlock(index, { ...block, content: { ...block.content, image_position: v } })}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Image Left</SelectItem>
                  <SelectItem value="right">Image Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case 'stats':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Stats (JSON format: [{'"'}value{'"'}: {'"'}500+{'"'}, {'"'}label{'"'}: {'"'}Products{'"'}])</Label>
              <Textarea
                value={block.content?.stats || '[{"value": "500+", "label": "Products"}, {"value": "2000+", "label": "Customers"}]'}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, stats: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700 font-mono text-xs"
                rows={4}
              />
            </div>
            <div>
              <Label className="text-xs">Background Color</Label>
              <Input
                value={block.content?.bg_color || '#78BE20'}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, bg_color: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        );
      case 'features':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Section Title</Label>
              <Input
                value={block.content?.title || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Features (JSON array with title, description, icon fields)</Label>
              <Textarea
                value={block.content?.features || '[{"title": "Quality", "description": "Premium products", "icon": "Shield"}]'}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, features: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700 font-mono text-xs"
                rows={4}
              />
            </div>
          </div>
        );
      case 'testimonials':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Section Title</Label>
              <Input
                value={block.content?.title || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Testimonials (JSON array with quote, author, company fields)</Label>
              <Textarea
                value={block.content?.testimonials || '[{"quote": "Great service!", "author": "John Smith", "company": "ABC Corp"}]'}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, testimonials: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700 font-mono text-xs"
                rows={4}
              />
            </div>
          </div>
        );
      case 'faq':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Section Title</Label>
              <Input
                value={block.content?.title || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">FAQs (JSON array with question, answer fields)</Label>
              <Textarea
                value={block.content?.faqs || '[{"question": "What are your shipping options?", "answer": "We ship Australia-wide..."}]'}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, faqs: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700 font-mono text-xs"
                rows={4}
              />
            </div>
          </div>
        );
      case 'contact_form':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Form Title</Label>
              <Input
                value={block.content?.title || 'Contact Us'}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Email Recipient</Label>
              <Input
                value={block.content?.email || 'sales@techdistribution.com.au'}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, email: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        );
      case 'map':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Google Maps Embed URL</Label>
              <Input
                value={block.content?.embed_url || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, embed_url: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="https://www.google.com/maps/embed?..."
              />
            </div>
            <div>
              <Label className="text-xs">Height (px)</Label>
              <Input
                type="number"
                value={block.content?.height || 400}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, height: parseInt(e.target.value) } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        );
      case 'video':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Video Embed URL (YouTube/Vimeo)</Label>
              <Input
                value={block.content?.embed_url || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, embed_url: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="https://www.youtube.com/embed/..."
              />
            </div>
            <div>
              <Label className="text-xs">Title (optional)</Label>
              <Input
                value={block.content?.title || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        );
      case 'spacer':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Height (px)</Label>
              <Input
                type="number"
                value={block.content?.height || 60}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, height: parseInt(e.target.value) } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        );
      case 'divider':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Style</Label>
              <Select 
                value={block.content?.style || 'line'} 
                onValueChange={(v) => updateBlock(index, { ...block, content: { ...block.content, style: v } })}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="line">Simple Line</SelectItem>
                  <SelectItem value="dashed">Dashed Line</SelectItem>
                  <SelectItem value="gradient">Gradient</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case 'product_carousel':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Section Title</Label>
              <Input
                value={block.content?.title || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Category Filter (optional slug)</Label>
              <Input
                value={block.content?.category || ''}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, category: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="e.g., tachyon-networks"
              />
            </div>
            <div>
              <Label className="text-xs">Number of Products</Label>
              <Input
                type="number"
                value={block.content?.count || 8}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, count: parseInt(e.target.value) } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        );
      case 'vendor_showcase':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Section Title</Label>
              <Input
                value={block.content?.title || 'Our Vendor Partners'}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, title: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Subtitle</Label>
              <Input
                value={block.content?.subtitle || 'Industry-leading brands we proudly distribute'}
                onChange={(e) => updateBlock(index, { ...block, content: { ...block.content, subtitle: e.target.value } })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-xs">Layout Style</Label>
              <Select
                value={block.content?.layout || 'cards'}
                onValueChange={(v) => updateBlock(index, { ...block, content: { ...block.content, layout: v } })}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cards">Cards with Details</SelectItem>
                  <SelectItem value="logos">Logo Strip</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-zinc-800 border border-zinc-700 rounded-sm p-4">
      <div className="flex items-center gap-4 mb-4">
        <button {...attributes} {...listeners} className="cursor-grab text-zinc-500 hover:text-zinc-300">
          <GripVertical className="h-5 w-5" />
        </button>
        <span className="text-[#78BE20] font-medium text-sm uppercase">
          {BLOCK_TYPES.find(t => t.value === block.type)?.label || block.type}
        </span>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => removeBlock(index)}
          className="text-zinc-500 hover:text-red-500"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {renderBlockFields()}
    </div>
  );
}

// Inline Vendor Manager for Page Builder
function VendorManager() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', logo_url: '', website: '', about_text: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API}/admin/vendors`, { headers });
      setVendors(res.data.vendors || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVendors(); }, []);

  const openAdd = () => {
    setEditingVendor(null);
    setForm({ name: '', description: '', logo_url: '', website: '', about_text: '' });
    setShowDialog(true);
  };

  const openEdit = (vendor) => {
    setEditingVendor(vendor);
    setForm({
      name: vendor.name || '',
      description: vendor.description || '',
      logo_url: vendor.logo_url || '',
      website: vendor.website || '',
      about_text: vendor.about_text || '',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingVendor) {
        await axios.put(`${API}/admin/vendors/${editingVendor.id}`, form, { headers });
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API}/admin/vendors`, form, { headers });
        toast.success('Vendor added');
      }
      setShowDialog(false);
      fetchVendors();
    } catch (err) {
      toast.error('Failed to save vendor');
    }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Delete "${vendor.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/vendors/${vendor.id}`, { headers });
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="h-24 bg-zinc-800 rounded animate-pulse" />;

  return (
    <div className="bg-[#18181b] border border-zinc-800 rounded-sm mb-6">
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <Store className="h-4 w-4 text-[#78BE20]" />
          <h3 className="text-white font-medium">Vendors on this page</h3>
          <span className="text-xs text-zinc-500">({vendors.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <a href="/admin/vendors" target="_blank" rel="noopener noreferrer" className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
            Full Manager <ExternalLink className="h-3 w-3" />
          </a>
          <Button size="sm" onClick={openAdd} className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
            <Plus className="h-3 w-3 mr-1" /> Add Vendor
          </Button>
        </div>
      </div>

      <div className="divide-y divide-zinc-800">
        {vendors.length === 0 ? (
          <div className="p-8 text-center text-zinc-500">
            No vendors yet. Add your first vendor to get started.
          </div>
        ) : vendors.map(vendor => (
          <div key={vendor.id} className="flex items-center gap-4 p-4 hover:bg-zinc-900/50 group" data-testid={`pb-vendor-${vendor.slug}`}>
            {vendor.logo_url && (
              <div className="h-8 w-20 shrink-0">
                <img src={vendor.logo_url} alt="" className="h-full w-auto object-contain opacity-60 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-white truncate">{vendor.name}</span>
                {vendor.enabled === false && <span className="text-[10px] bg-zinc-700 text-zinc-400 px-1.5 py-0.5 rounded">HIDDEN</span>}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {vendor.resources?.length || 0} resources &middot; {vendor.features?.length || 0} features
                {vendor.website && <> &middot; <span className="text-zinc-400">{vendor.website.replace(/^https?:\/\/(www\.)?/, '')}</span></>}
              </div>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button variant="ghost" size="sm" onClick={() => openEdit(vendor)} className="h-7 px-2 text-zinc-400 hover:text-white">
                <Pencil className="h-3 w-3" />
              </Button>
              <a href={`/admin/vendors`} target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm" className="h-7 px-2 text-zinc-400 hover:text-white" title="Manage resources & features">
                  <Link2 className="h-3 w-3" />
                </Button>
              </a>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(vendor)} className="h-7 px-2 text-zinc-400 hover:text-red-400">
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs">Vendor Name *</Label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700" placeholder="e.g. Racom" />
            </div>
            <div>
              <Label className="text-xs">Short Description</Label>
              <Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700" placeholder="One-line tagline" />
            </div>
            <div>
              <Label className="text-xs">Logo URL</Label>
              <Input value={form.logo_url} onChange={(e) => setForm(f => ({ ...f, logo_url: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700" placeholder="/vendor-logos/logo.svg" />
            </div>
            <div>
              <Label className="text-xs">Website</Label>
              <Input value={form.website} onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700" placeholder="https://example.com" />
            </div>
            <div>
              <Label className="text-xs">About Text</Label>
              <Textarea value={form.about_text} onChange={(e) => setForm(f => ({ ...f, about_text: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700" rows={4} placeholder="Detailed description of the vendor..." />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="border-zinc-700">Cancel</Button>
              <Button onClick={handleSave} disabled={!form.name} className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
                {editingVendor ? 'Save Changes' : 'Add Vendor'}
              </Button>
            </div>
            {editingVendor && (
              <p className="text-[11px] text-zinc-500 text-center">
                To manage resources & features, use the <a href="/admin/vendors" target="_blank" className="text-[#78BE20] underline">full Vendors Manager</a>
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Inline Project Manager for Page Builder
function ProjectManager() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', tags: '', gallery: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/admin/projects`, { headers });
      setProjects(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const openAdd = () => {
    setEditingProject(null);
    setForm({ title: '', description: '', tags: '', gallery: '' });
    setShowDialog(true);
  };

  const openEdit = (project) => {
    setEditingProject(project);
    setForm({
      title: project.title || '',
      description: project.description || '',
      tags: (project.tags || []).join(', '),
      gallery: (project.gallery || []).join('\n'),
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      gallery: form.gallery.split('\n').map(u => u.trim()).filter(Boolean),
    };
    try {
      if (editingProject) {
        await axios.put(`${API}/admin/projects/${editingProject.id}`, payload, { headers });
        toast.success('Project updated');
      } else {
        await axios.post(`${API}/admin/projects`, payload, { headers });
        toast.success('Project added');
      }
      setShowDialog(false);
      fetchProjects();
    } catch (err) {
      toast.error('Failed to save project');
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Delete "${project.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/projects/${project.id}`, { headers });
      toast.success('Project deleted');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="h-24 bg-zinc-800 rounded animate-pulse" />;

  return (
    <div className="bg-[#18181b] border border-zinc-800 rounded-sm mb-6">
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <Store className="h-4 w-4 text-[#78BE20]" />
          <h3 className="text-white font-medium">Projects on this page</h3>
          <span className="text-xs text-zinc-500">({projects.length})</span>
        </div>
        <Button size="sm" onClick={openAdd} className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
          <Plus className="h-3 w-3 mr-1" /> Add Project
        </Button>
      </div>

      <div className="divide-y divide-zinc-800">
        {projects.length === 0 ? (
          <div className="p-8 text-center text-zinc-500">
            No projects yet. Add your first project to get started.
          </div>
        ) : projects.map(project => (
          <div key={project.id} className="flex items-center gap-4 p-4 hover:bg-zinc-900/50 group" data-testid={`pb-project-${project.slug}`}>
            {project.gallery?.[0] && (
              <div className="h-12 w-16 rounded overflow-hidden shrink-0 bg-zinc-800">
                <img src={project.gallery[0]} alt="" className="h-full w-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <span className="font-medium text-white truncate block">{project.title}</span>
              <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
                {project.tags?.slice(0, 4).map(tag => (
                  <span key={tag} className="bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-400">{tag}</span>
                ))}
                {project.gallery && <span>&middot; {project.gallery.length} images</span>}
              </div>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button variant="ghost" size="sm" onClick={() => openEdit(project)} className="h-7 px-2 text-zinc-400 hover:text-white">
                <Pencil className="h-3 w-3" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(project)} className="h-7 px-2 text-zinc-400 hover:text-red-400">
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Edit Project' : 'Add Project'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs">Project Title *</Label>
              <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700" placeholder="e.g. Remote Solar Telecom Site" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700" rows={3} placeholder="Describe the project..." />
            </div>
            <div>
              <Label className="text-xs">Tags (comma-separated)</Label>
              <Input value={form.tags} onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700" placeholder="Solar, Telecommunications, Off-grid" />
            </div>
            <div>
              <Label className="text-xs">Gallery Image URLs (one per line)</Label>
              <Textarea value={form.gallery} onChange={(e) => setForm(f => ({ ...f, gallery: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700" rows={3} placeholder="https://example.com/image1.jpg" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="border-zinc-700">Cancel</Button>
              <Button onClick={handleSave} disabled={!form.title} className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
                {editingProject ? 'Save Changes' : 'Add Project'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Inline Product/Category Manager for Page Builder
function ProductManager() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/products`, { headers }).catch(() => ({ data: { products: [] } })),
      axios.get(`${API}/categories`).catch(() => ({ data: [] })),
    ]).then(([prodRes, catRes]) => {
      setProducts(prodRes.data.products || prodRes.data || []);
      setCategories(catRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="h-24 bg-zinc-800 rounded animate-pulse" />;

  return (
    <div className="bg-[#18181b] border border-zinc-800 rounded-sm mb-6">
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <Store className="h-4 w-4 text-[#78BE20]" />
          <h3 className="text-white font-medium">Products & Categories</h3>
          <span className="text-xs text-zinc-500">({products.length} products, {categories.length} categories)</span>
        </div>
        <a href="/admin/products" target="_blank" rel="noopener noreferrer">
          <Button size="sm" className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
            <ExternalLink className="h-3 w-3 mr-1" /> Manage Products
          </Button>
        </a>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <Label className="text-xs text-zinc-500 uppercase tracking-wider">Categories</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {categories.map(cat => (
              <span key={cat.id} className="text-xs bg-zinc-800 border border-zinc-700 px-2.5 py-1 rounded text-zinc-300">
                {cat.name} <span className="text-zinc-500">({cat.product_count || 0})</span>
              </span>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs text-zinc-500 uppercase tracking-wider">Recent Products</Label>
          <div className="divide-y divide-zinc-800 mt-2 max-h-[200px] overflow-y-auto">
            {products.slice(0, 8).map(p => (
              <div key={p.id} className="flex items-center gap-3 py-2">
                {p.images?.[0] && (
                  <div className="h-8 w-8 rounded bg-zinc-800 overflow-hidden shrink-0">
                    <img src={p.images[0]} alt="" className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-white truncate block">{p.name}</span>
                  <span className="text-xs text-zinc-500">{p.sku || 'No SKU'} &middot; Stock: {p.stock_quantity || 0}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPageBuilder() {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('home');
  const [pageData, setPageData] = useState({ title: '', meta_description: '', blocks: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchPages();
  }, []);

  useEffect(() => {
    fetchPage(selectedPage);
  }, [selectedPage]);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/admin/pages`);
      setPages(res.data);
    } catch (err) {
      console.error('Error fetching pages:', err);
    }
  };

  const fetchPage = async (slug) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pages/${slug}`);
      setPageData({
        title: res.data.title || slug,
        meta_description: res.data.meta_description || '',
        blocks: res.data.blocks || [],
      });
    } catch (err) {
      setPageData({ title: slug, meta_description: '', blocks: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${selectedPage}`, pageData);
      toast.success('Page saved');
      fetchPages();
    } catch (err) {
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      content: {},
      order: pageData.blocks.length,
    };
    setPageData(prev => ({ ...prev, blocks: [...prev.blocks, newBlock] }));
  };

  const updateBlock = (index, block) => {
    const newBlocks = [...pageData.blocks];
    newBlocks[index] = block;
    setPageData(prev => ({ ...prev, blocks: newBlocks }));
  };

  const removeBlock = (index) => {
    setPageData(prev => ({ ...prev, blocks: prev.blocks.filter((_, i) => i !== index) }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = pageData.blocks.findIndex(b => b.id === active.id);
      const newIndex = pageData.blocks.findIndex(b => b.id === over.id);
      setPageData(prev => ({
        ...prev,
        blocks: arrayMove(prev.blocks, oldIndex, newIndex).map((b, i) => ({ ...b, order: i })),
      }));
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-page-builder">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">
            Page Builder
          </h1>
          <Button onClick={handleSave} disabled={saving} className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Page'}
          </Button>
        </div>

        <div className="grid grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="col-span-1 space-y-6">
            {/* Page Selector */}
            <div className="bg-[#18181b] border border-zinc-800 rounded-sm p-4">
              <Label>Select Page</Label>
              <Select value={selectedPage} onValueChange={setSelectedPage}>
                <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="home">Home</SelectItem>
                  <SelectItem value="about">About</SelectItem>
                  <SelectItem value="contact">Contact</SelectItem>
                  <SelectItem value="products">Products</SelectItem>
                  <SelectItem value="vendors">Vendors</SelectItem>
                  <SelectItem value="projects">Projects</SelectItem>
                  <SelectItem value="account">My Account</SelectItem>
                  <SelectItem value="trade-application">Trade Application</SelectItem>
                  <SelectItem value="cart">Cart</SelectItem>
                  <SelectItem value="checkout">Checkout</SelectItem>
                  <SelectItem value="checkout-success">Checkout Success</SelectItem>
                  <SelectItem value="order-confirmation">Order Confirmation</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="register">Register</SelectItem>
                  <SelectItem value="forgot-password">Forgot Password</SelectItem>
                  <SelectItem value="reset-password">Reset Password</SelectItem>
                  <SelectItem value="terms">Terms & Conditions</SelectItem>
                  <SelectItem value="privacy">Privacy Policy</SelectItem>
                  <SelectItem value="shipping">Shipping Info</SelectItem>
                  <SelectItem value="returns">Returns Policy</SelectItem>
                  <SelectItem value="faq">FAQ</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Page Settings */}
            <div className="bg-[#18181b] border border-zinc-800 rounded-sm p-4 space-y-4">
              <h3 className="text-white font-medium">Page Settings</h3>
              <div>
                <Label className="text-xs">Page Title</Label>
                <Input
                  value={pageData.title}
                  onChange={(e) => setPageData(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-xs">Meta Description</Label>
                <Textarea
                  value={pageData.meta_description}
                  onChange={(e) => setPageData(prev => ({ ...prev, meta_description: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  rows={3}
                />
              </div>
            </div>

            {/* Add Block */}
            <div className="bg-[#18181b] border border-zinc-800 rounded-sm p-4">
              <h3 className="text-white font-medium mb-3">Add Block</h3>
              <div className="space-y-2">
                {BLOCK_TYPES.map(type => (
                  <Button
                    key={type.value}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start border-zinc-700 text-zinc-300 hover:text-white hover:border-[#78BE20]"
                    onClick={() => addBlock(type.value)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="col-span-3">
            {/* Page-specific content managers */}
            {selectedPage === 'vendors' && <VendorManager />}
            {selectedPage === 'projects' && <ProjectManager />}
            {selectedPage === 'products' && <ProductManager />}

            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-32 bg-zinc-800 rounded animate-pulse" />
                ))}
              </div>
            ) : pageData.blocks.length === 0 ? (
              <div className="bg-[#18181b] border border-dashed border-zinc-700 rounded-sm p-12 text-center">
                <p className="text-zinc-500">No blocks yet. Add blocks from the sidebar.</p>
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={pageData.blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-4">
                    {pageData.blocks.map((block, index) => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        index={index}
                        updateBlock={updateBlock}
                        removeBlock={removeBlock}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
